import { useRef, useState } from "react";
import { FolderOpen, Paperclip, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { DocumentPicker } from "@/components/documents/DocumentPicker";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type { DocumentVersioningTranslations } from "@/lib/i18n/types/documents-sections/versioning";
import { isBlockedFileType, isIgnoredFile } from "../../constants/fileTypes";
import { createAttachment, type TemplateFormAttachment } from "../../utils/templateForm";

/** The subset of a document this block needs to offer it as an attachment. */
export interface AttachableDocument {
  id: string;
  filename: string;
}

interface AttachmentsFieldProps {
  copy: TemplateFormTranslations;
  attachments: TemplateFormAttachment[];
  buildingId: string;
  /** Locks the picker to this project, which also hides its project dropdown
   *  (WHA-3393) — a form filed into a known project should not offer documents
   *  from a different one. `null` at building scope, where the project is not
   *  known until Save asks: there the dropdown appears and the user chooses. */
  projectId: string | null;
  onChange: (attachments: TemplateFormAttachment[]) => void;
}

/** A re-pick of an already-staged file (the input resets after every pick, so
 *  it fires again) would double the attachment on submit. A fresh pick is a
 *  new File object, so the match is by name+size+mtime — and announced, in
 *  case two genuinely different files happen to collide on all three. */
function isAlreadyStaged(attachments: TemplateFormAttachment[], file: File) {
  return attachments.some(
    (row) =>
      row.source === "upload" &&
      row.name === file.name &&
      row.file?.size === file.size &&
      row.file?.lastModified === file.lastModified
  );
}

/**
 * Drop OS junk silently, reject blocked types loudly, stage the rest with the
 * File itself — the gating every upload entry point applies. Returns the
 * attachments to append (empty when the pick had nothing usable).
 */
function stageFiles(
  files: FileList,
  attachments: TemplateFormAttachment[],
  copy: TemplateFormTranslations,
  blockedCopy: Pick<
    DocumentVersioningTranslations["addNewVersion"],
    "blockedTitle" | "blockedDescription"
  >
): TemplateFormAttachment[] {
  const candidates = Array.from(files).filter((file) => !isIgnoredFile(file));
  const blocked = candidates.filter(isBlockedFileType);
  const allowed = candidates.filter((file) => !isBlockedFileType(file));
  const duplicates = allowed.filter((file) => isAlreadyStaged(attachments, file));
  const accepted = allowed.filter((file) => !isAlreadyStaged(attachments, file));

  if (blocked.length > 0) {
    toast.error(blockedCopy.blockedTitle, {
      description: blockedCopy.blockedDescription.replace("{count}", String(blocked.length)),
    });
  }
  if (duplicates.length > 0) {
    toast.info(copy.duplicatesSkipped.replace("{count}", String(duplicates.length)));
  }
  return accepted.map((file) => createAttachment(file.name, "upload", undefined, file));
}

export function AttachmentsField({
  copy,
  attachments,
  buildingId,
  projectId,
  onChange,
}: AttachmentsFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { t } = useLanguage();

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const staged = stageFiles(files, attachments, copy, t.documents.versioning.addNewVersion);
    if (staged.length > 0) onChange([...attachments, ...staged]);
  };

  return (
    <div className="space-y-3">
      <AttachmentList
        copy={copy}
        attachments={attachments}
        onRemove={(rowId) => onChange(attachments.filter((row) => row.rowId !== rowId))}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload aria-hidden className="h-4 w-4" />
          {copy.uploadFiles}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            addFiles(event.target.files);
            // Reset, so picking the same file twice in a row still fires.
            event.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setPickerOpen(true)}
        >
          <FolderOpen aria-hidden className="h-4 w-4" />
          {copy.chooseFromProject}
        </Button>
      </div>

      {/* The same picker the chatbot's "Add from Franklin" opens: folder tree,
          search and multi-select, rather than a flat list in a popover. */}
      {pickerOpen && (
        <DocumentPicker
          open
          onOpenChange={setPickerOpen}
          buildingId={buildingId}
          // Omitted when the page has no project scope, which un-hides the
          // picker's own project dropdown. Gating the whole dialog on a project
          // instead meant the button did nothing at all.
          projectId={projectId ?? undefined}
          mode="multi"
          title={copy.chooseFromProject}
          // Attached documents come back ticked and are excluded from the
          // count, so the same file cannot be attached twice.
          alreadyAttachedIds={attachments
            .map((row) => row.documentId)
            .filter((id): id is string => Boolean(id))}
          onSelect={(picked, pickedProjectId) => {
            const fresh = picked.filter(
              (doc) => !attachments.some((row) => row.documentId === doc.id)
            );
            if (fresh.length > 0) {
              onChange([
                ...attachments,
                // The source project rides along on each row, so at building
                // scope the destination picker can default to the project the
                // attachments were picked from.
                ...fresh.map((doc) =>
                  createAttachment(
                    doc.filename,
                    "project",
                    doc.id,
                    undefined,
                    pickedProjectId ?? undefined
                  )
                ),
              ]);
            }
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

interface AttachmentListProps {
  copy: TemplateFormTranslations;
  attachments: TemplateFormAttachment[];
  onRemove: (rowId: string) => void;
}

function AttachmentList({ copy, attachments, onRemove }: AttachmentListProps) {
  if (attachments.length === 0) {
    return <p className="text-sm text-muted-foreground">{copy.noAttachments}</p>;
  }

  return (
    <ul className="space-y-1">
      {attachments.map((attachment) => (
        <li
          key={attachment.rowId}
          className="flex items-center gap-2 border-b py-1.5 text-sm last:border-b-0"
        >
          <Paperclip aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{attachment.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={copy.removeAttachment}
            onClick={() => onRemove(attachment.rowId)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
